import { DateService, NumberFormatterService } from "@/core/services";
import { NumberFormatType } from "@/core/types";
import { DateAdapter } from "@/infrastructure/adapters/dayJs";
import { NumberFormatterAdapter } from "@/infrastructure/adapters/number";
import {
  DeductionDocument,
  DeductionPriorityProps,
  DeductionStageProps,
  DeductionStatusProps,
  DeductionUserProps,
  DeductionValidityProps,
  DeductionDisputeRequestProps,
  DeductionDisputeResponseProps,
} from "..";
import { Purchaser, PurchaserProps } from "@/core/entities/lookup";

export enum CategoryKey {
  DEDUCTION = "DEDUCTION",
  REMITTANCE_ITEM = "REMITTANCE_ITEM",
  CREDIT = "CREDIT",
  REPAYMENT = "REPAYMENT",
  PREPAYMENT = "PREPAYMENT",
}
export enum CategoryTypeKey {
  APPROVAL = "APPROVAL",
  DISPUTE = "DISPUTE",
}

export interface AdjustmentTypeProps {
  id: number;
  typeName: string;
  typeKey: string;
}

export interface AdjustmentCategoryProps {
  id: number;
  categoryName: string;
  categoryKey: string;
}

export interface DeductionReturnedProps {
  returnedBy: DeductionUserProps;
  returnedAt: string;
  returnedMessage: string;
}

export interface RemittanceItemProps {
  deductionCount?: number | null;
  invoiceNo: string;
  invoiceDate: string;
  grossAmount: number;
  netAmount: number;
  discountAmount: number | null;
  purchaser?: PurchaserProps | null;
  description?: string | null;
  isDeduction: boolean;
  remittanceId: number;
  remittanceItemId: number;
  checkNo?: string | null;
  priority?: DeductionPriorityProps | null;
  validity?: DeductionValidityProps | null;
  stage?: DeductionStageProps | null;
  status?: DeductionStatusProps | null;
  age?: number | null;
  adjustmentType?: AdjustmentTypeProps | null;
  adjustmentCategory?: AdjustmentCategoryProps | null;
  analystUser?: DeductionUserProps | null;
  approverUser?: DeductionUserProps | null;
  isIncompleteDeduction?: boolean;
  isManuallyUploaded?: boolean;
  isBackupRequested?: boolean;
  canRequestBackup?: boolean;
  isDisputed?: boolean;
  deductionDispute?: DeductionDisputeProps[] | null;
  returned?: DeductionReturnedProps | null;
}

export interface DeductionDisputeProps {
  disputeRequest: DeductionDisputeRequestProps;
  disputeResponse: DeductionDisputeResponseProps;
}

export class RemittanceItem {
  private readonly _deductionCount: number | null;
  private readonly _invoiceNo: string;
  private readonly _invoiceDate: string;
  private readonly _grossAmount: number;
  private readonly _netAmount: number;
  private readonly _discountAmount: number | null;
  private readonly _purchaser: PurchaserProps | null;
  private readonly _description: string | null;
  private readonly _isDeduction: boolean | null;
  private readonly _remittanceId: number;
  private readonly _remittanceItemId: number;
  private readonly _checkNo?: string | null;
  private readonly _priority: DeductionPriorityProps | null;
  private readonly _validity: DeductionValidityProps | null;
  private readonly _stage: DeductionStageProps | null;
  private readonly _status: DeductionStatusProps | null;
  private readonly _age: number | null;
  private readonly _adjustmentType: AdjustmentTypeProps | null;
  private readonly _adjustmentCategory: AdjustmentCategoryProps | null;
  private readonly _analystUser: DeductionUserProps | null;
  private readonly _approverUser: DeductionUserProps | null;
  private readonly _isIncompleteDeduction?: boolean;
  private readonly _isManuallyUploaded?: boolean;
  private _isBackupRequested?: boolean;
  private readonly _canRequestBackup?: boolean;
  private readonly _isDisputed?: boolean;
  private readonly _deductionDispute?: DeductionDisputeProps[] | null;
  private readonly _returned?: DeductionReturnedProps | null;

  constructor(props: RemittanceItemProps) {
    this._deductionCount = props.deductionCount ?? null;
    this._invoiceNo = props.invoiceNo;
    this._invoiceDate = props.invoiceDate;
    this._grossAmount = props.grossAmount;
    this._netAmount = props.netAmount;
    this._discountAmount = props.discountAmount ?? null;
    this._purchaser = props.purchaser ?? null;
    this._description = props.description ?? null;
    this._isDeduction = props.isDeduction ?? null;
    this._remittanceId = props.remittanceId;
    this._remittanceItemId = props.remittanceItemId;
    this._checkNo = props.checkNo;
    this._priority = props.priority ?? null;
    this._validity = props.validity ?? null;
    this._stage = props.stage ?? null;
    this._status = props.status ?? null;
    this._age = props.age ?? null;
    this._adjustmentType = props.adjustmentType ?? null;
    this._adjustmentCategory = props.adjustmentCategory ?? null;
    this._analystUser = props.analystUser ?? null;
    this._approverUser = props.approverUser ?? null;
    this._isIncompleteDeduction = props.isIncompleteDeduction ?? false;
    this._isManuallyUploaded = props.isManuallyUploaded ?? false;
    this._isBackupRequested = props.isBackupRequested ?? false;
    this._canRequestBackup = props.canRequestBackup ?? false;
    this._isDisputed = props.isDisputed ?? false;
    this._deductionDispute = props.deductionDispute ?? null;
    this._returned = props.returned ?? null;
  }

  get key(): number {
    return this._remittanceItemId;
  }

  get deductionCount(): number | null {
    return this._deductionCount ?? null;
  }

  get invoiceNumber(): string {
    return this._invoiceNo;
  }

  get invoiceDate(): string {
    return this._invoiceDate;
  }

  public invoiceDateFormatted(format: string): string {
    return new DateService(new DateAdapter()).format(this._invoiceDate, format);
  }

  get grossAmount(): number {
    return this._grossAmount;
  }

  public grossAmountFormatted(format: NumberFormatType): string {
    return new NumberFormatterService(new NumberFormatterAdapter()).format(this._grossAmount, format);
  }

  get netAmount(): number {
    return this._netAmount;
  }

  public netAmountFormatted(format: NumberFormatType): string {
    return new NumberFormatterService(new NumberFormatterAdapter()).format(this._netAmount, format);
  }

  get discountAmount(): number | null {
    return this._discountAmount;
  }

  public discountAmountFormatted(format: NumberFormatType): string {
    return new NumberFormatterService(new NumberFormatterAdapter()).format(this._discountAmount ?? 0, format);
  }

  get purchaser(): PurchaserProps | null {
    return this._purchaser ? new Purchaser(this._purchaser) : null;
  }

  get description(): string | null {
    return this._description ?? null;
  }

  get isDeduction(): boolean | null {
    return this._isDeduction ?? null;
  }

  get remittanceId(): number {
    return this._remittanceId;
  }

  get remittanceItemId(): number {
    return this._remittanceItemId;
  }

  get checkNo(): string | null {
    return this._checkNo ?? null;
  }

  get priority(): DeductionPriorityProps | null {
    return this._priority ?? null;
  }

  get validity(): DeductionValidityProps | null {
    return this._validity ?? null;
  }

  get stage(): DeductionStageProps | null {
    return this._stage ?? null;
  }

  get status(): DeductionStatusProps | null {
    return this._status ?? null;
  }

  get age(): number | null {
    return this._age ?? null;
  }

  get adjustmentType(): AdjustmentTypeProps | null {
    return this._adjustmentType ?? null;
  }

  get adjustmentCategory(): AdjustmentCategoryProps | null {
    return this._adjustmentCategory ?? null;
  }

  get analystUser(): DeductionUserProps | null {
    return this._analystUser ?? null;
  }

  get approverUser(): DeductionUserProps | null {
    return this._approverUser ?? null;
  }

  get isIncompleteDeduction(): boolean {
    return this._isIncompleteDeduction ?? false;
  }

  get isManuallyUploaded(): boolean {
    return this._isManuallyUploaded ?? false;
  }

  get isBackupRequested(): boolean {
    return this._isBackupRequested ?? false;
  }

  set isBackupRequested(isBackupRequested: boolean) {
    this._isBackupRequested = isBackupRequested;
  }

  get canRequestBackup(): boolean {
    return this._canRequestBackup ?? false;
  }

  get isDisputed(): boolean {
    return this._isDisputed ?? false;
  }

  get deductionDispute(): DeductionDisputeProps[] | null {
    return this._deductionDispute ?? null;
  }

  get returned(): DeductionReturnedProps | null {
    return this._returned ?? null;
  }

  get isCredit(): boolean {
    return this._isDeduction === false && this._adjustmentCategory?.categoryKey === "CREDIT";
  }

  get isRepayment(): boolean {
    return this._isDeduction === false && this._adjustmentCategory?.categoryKey === "REPAYMENT";
  }
}

export interface IncompleteDeductionResolutionStatusProps {
  remittanceItemId: number;
  documents: DeductionDocument[];
  purchaserKey: string;
}

export class IncompleteDeductionResolutionStatus {
  private readonly _remittanceItemId: number;
  private readonly _purchaserKey: string;
  private readonly _documents: DeductionDocument[];

  constructor(props: IncompleteDeductionResolutionStatusProps) {
    this._remittanceItemId = props.remittanceItemId;
    this._purchaserKey = props.purchaserKey;
    this._documents = props.documents;
  }

  get remittanceItemId(): number {
    return this._remittanceItemId;
  }

  get purchaserKey(): string {
    return this._purchaserKey;
  }

  get documents(): DeductionDocument[] {
    return this._documents;
  }
}
